import { readAnnouncer } from "./announcer-codec.js";
// SL v1. Deliberately independent of Three.js, workers, and network APIs for fixture testing.
export const Message = Object.freeze({ Hello: 1, Welcome: 2, MapBegin: 3, MapEnd: 4,
    Status: 5, Dictionary: 6, SnapshotBegin: 7, SnapshotChunk: 8, SnapshotEnd: 9,
    Delta: 10, Ack: 11, Heartbeat: 12, ResyncRequest: 13, Subscribe: 14, Ready: 15, Unsubscribe: 16, VoicePacket: 17, VoiceAudio: 18, VoiceSubscribe: 19, VoiceUnsubscribe: 20, ViewerPosition: 21, ViewerChat: 22, ViewerRoster: 23, ViewerAccess: 24 });
export const Component = Object.freeze({ Transform: 1, Appearance: 2, Parent: 4, Player: 8, Animation: 16, Building: 32, Light: 64, Particle: 128, Projectile: 256, Effects: 512, Event: 1024, Scoreboard: 2048, Sound: 4096, PlayerAnimation: 8192, Ragdoll: 16384, Spray: 32768, Sprite: 65536, AnimationLayers: 131072, Text: 262144, AnnouncerState: 524288, WorldText: 1048576, DeathNotice: 2097152, Annotation: 4194304, ItemFound: 8388608, Teleport: 16777216 });
export const Status = Object.freeze({ Disconnected: 0, AwaitingSnapshot: 1, Loading: 2, Live: 3, Stale: 4, Idle: 5, MapUnavailable: 6, MapChanging: 7 });
export const limits = Object.freeze({ frame: 65536, snapshot: 4194304, entities: 4096, dictionary: 16384, snapshotMs: 10000 });
const decoder = new TextDecoder("utf-8", { fatal: true, ignoreBOM: true });
const encoder = new TextEncoder();
const invalidString = (value, colors) => /[\x00-\x1f\x7f-\x9f]/.test(colors
    ? value.replace(/\x07[a-f0-9]{6}|\x08[a-f0-9]{8}|[\x01-\x06]/gi, '') : value);
export class Reader {
    constructor(bytes) {
        this.bytes = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
        this.view = new DataView(this.bytes.buffer, this.bytes.byteOffset, this.bytes.byteLength);
        this.offset = 0;
    }
    take(size) {
        if (!Number.isSafeInteger(size) || size < 0 || size > this.bytes.length - this.offset) throw new Error("Truncated live payload");
        const start = this.offset; this.offset += size; return this.bytes.subarray(start, this.offset);
    }
    u8() { return this.take(1)[0]; }
    u16() { const o = this.offset; this.take(2); return this.view.getUint16(o, true); }
    u32() { const o = this.offset; this.take(4); return this.view.getUint32(o, true); }
    i32() { const o = this.offset; this.take(4); return this.view.getInt32(o, true); }
    u64() { const o = this.offset; this.take(8); return this.view.getBigUint64(o, true); }
    f32() {
        const o = this.offset; this.take(4); const value = this.view.getFloat32(o, true);
        if (!Number.isFinite(value)) throw new Error("Non-finite live float");
        return value;
    }
    string(colors = false) {
        const length = this.u16(); if (length > 512) throw new Error("Live string too large");
        const value = decoder.decode(this.take(length));
        if (invalidString(value, colors)) throw new Error("Invalid live string");
        return value;
    }
    end() { if (this.offset !== this.bytes.length) throw new Error("Unexpected live payload bytes"); }
}

export class Writer {
    constructor() { this.bytes = []; }
    u8(v) { this.bytes.push(v & 255); return this; }
    u16(v) { return this.u8(v).u8(v >>> 8); }
    u32(v) { return this.u16(v).u16(v >>> 16); }
    u64(v) { return this.u32(Number(v & 0xffffffffn)).u32(Number(v >> 32n)); }
    raw(bytes) { for (const b of bytes) this.u8(b); return this; }
    string(value, colors = false) {
        const bytes = encoder.encode(value);
        if (bytes.length > 512 || invalidString(value, colors)) throw new Error("Invalid live string");
        return this.u16(bytes.length).raw(bytes);
    }
    finish() { return new Uint8Array(this.bytes); }
}

export function decodeFrame(bytes) {
    const r = new Reader(bytes);
    if (r.bytes.length < 24 || r.bytes.length > limits.frame || r.u16() !== 0x4c53 || r.u8() !== 1) throw new Error("Unsupported live frame");
    const type = r.u8(), epoch = r.u64(), sequence = r.u32(), sampleTime = r.u32(), flags = r.u32();
    if (type < 1 || type > 24 || flags !== 0) throw new Error("Unsupported live protocol header");
    return { type, epoch, sequence, sampleTime, payload: r.take(r.bytes.length - r.offset) };
}

export function encodeFrame({ type, epoch = 0n, sequence = 0, sampleTime = 0, payload = new Uint8Array() }) {
    if (payload.length > limits.frame - 24) throw new Error("Live frame too large");
    return new Writer().u16(0x4c53).u8(1).u8(type).u64(epoch).u32(sequence).u32(sampleTime).u32(0).raw(payload).finish();
}

export function checksum(bytes, previous = 2166136261) {
    for (const b of bytes) previous = Math.imul(previous ^ b, 16777619) >>> 0;
    return previous;
}

export function validateDictionary(kind, id, value) {
    if (![1, 2, 3, 4].includes(kind) || id === 0 || !value.trim()) throw new Error("Invalid dictionary entry");
    if (kind === 4 && (!value.startsWith("sound/") || !/\.(wav|mp3)$/.test(value) || /[A-Z:\\]/.test(value)
        || value.split("/").some(s => !s || s === "." || s === ".."))) throw new Error("Invalid sound path");
    if (kind === 1) {
        if (/^\*\d+$/.test(value) && Number(value.slice(1)) > 0 && Number(value.slice(1)) < 65536) return;
        if (!/\.(mdl|spr|vmt|vtf)$/.test(value) || /[A-Z:\\]/.test(value)
            || value.split("/").some(s => !s || s === "." || s === "..")) throw new Error("Invalid model path");
    }
}

export function decodeComponent(kind, bytes) {
    const r = new Reader(bytes), data = {};
    switch (kind) {
        case Component.Annotation:
            data.id = r.i32(); data.time = r.u32(); data.lifetime = r.f32(); data.text = r.string();
            data.position = [r.f32(), r.f32(), r.f32()]; data.followIndex = r.u32(); data.followGeneration = r.u32(); data.flags = r.u8();
            if (data.lifetime < -1 || data.lifetime > 86400 || data.followIndex > 65535 || (!!data.followIndex !== !!data.followGeneration) || data.flags > 1) throw new Error('Invalid annotation');
            break;
        case Component.ItemFound:
            data.time = r.u32(); data.team = r.u8(); data.name = r.string(); data.item = r.u16(); data.quality = r.u8(); data.method = r.u8(); data.flags = r.u8(); data.wear = r.f32();
            if (data.team > 3 || data.quality > 15 || data.method > 31 || data.flags > 3 || data.wear < 0 || data.wear > 1) throw new Error('Invalid item announcement');
            break;
        case Component.Teleport:
            data.flags = r.u8(); data.destination = [r.f32(), r.f32(), r.f32()]; data.angles = [r.f32(), r.f32(), r.f32()]; data.landmark = [r.f32(), r.f32(), r.f32()];
            if (data.flags > 15) throw new Error('Invalid teleport');
            break;
        case Component.Transform:
            data.position = [r.i32(), r.i32(), r.i32()].map(v => v / 16);
            data.angles = [r.u16(), r.u16(), r.u16()].map(v => v * 360 / 65536);
            data.velocity = [r.i32(), r.i32(), r.i32()].map(v => v / 16);
            data.discontinuity = r.u8(); if (data.discontinuity > 1) throw new Error("Invalid transform flags");
            break;
        case Component.Appearance:
            data.model = r.u32(); data.netClass = r.u32(); data.category = r.u8();
            data.skin = r.i32(); data.body = r.u32(); data.scale = r.f32();
            data.color = r.u32(); data.effects = r.u32(); data.renderMode = r.u8();
            if (data.category > 9 || data.scale <= 0 || data.scale > 1024) throw new Error("Invalid appearance");
            if (r.offset < r.bytes.length) {
                if (r.u8() !== 1) throw new Error('Unsupported viewer owner extension');
                data.viewerSteamId = r.string();
                if (!/^(?:7656119[0-9]{10}|a[0-9a-f]{16})$/.test(data.viewerSteamId)) throw new Error('Invalid viewer owner');
            }
            break;
        case Component.Parent:
            data.index = r.u32(); data.generation = r.u32(); data.attachment = r.u16(); data.flags = r.u8();
            if (data.flags > 7) throw new Error("Invalid attachment flags");
            if (data.flags & 4) {
                data.localPosition = [r.i32(), r.i32(), r.i32()].map(v => v / 16);
                data.localAngles = [r.u16(), r.u16(), r.u16()].map(v => v * 360 / 65536);
            }
            break;
        case Component.Player:
            data.team = r.u8(); data.playerClass = r.u8(); data.state = r.u16();
            data.eyeAngles = [r.u16(), r.u16()].map(v => v * 360 / 65536);
            data.weaponIndex = r.u32(); data.weaponGeneration = r.u32(); data.conditions = [r.u32(), r.u32(), r.u32(), r.u32()];
            if (r.offset < r.bytes.length) {
                data.health = r.u16(); data.maxHealth = r.u16();
                if (!data.maxHealth) throw new Error("Invalid player health");
            }
            if (r.offset < r.bytes.length) data.conditions.push(r.u32());
            if (data.team > 3 || data.playerClass > 9) throw new Error("Invalid player team/class");
            break;
        case Component.AnnouncerState:
            Object.assign(data, readAnnouncer(r)); break;
        case Component.WorldText:
            data.text = r.string(true); data.size = r.f32(); data.orientation = r.u8(); data.font = r.u8(); data.color = r.u32();
            if (data.size <= 0 || data.size > 1024 || data.orientation > 2 || data.font > 12) throw new Error('Invalid world text');
            break;
        case Component.DeathNotice:
            data.startTime = r.u32(); data.kind = r.u8(); data.flags = r.u16(); data.custom = r.u16(); data.damage = r.u32();
            data.actors = Array.from({ length: 3 }, () => ({ userid: r.u32(), team: r.u8(), name: r.string(true) }));
            data.weapon = r.string(true); data.object = r.u8();
            if (data.kind > 4 || data.actors.some(a => a.team > 3) || bytes.length > 640) throw new Error('Invalid death notice');
            break;
        case Component.Text:
            data.kind = r.u8(); data.team = r.u8(); data.startTime = r.u32();
            data.format = r.string(true); data.params = Array.from({ length: 4 }, () => r.string(true));
            if (data.kind > 4 || data.team > 3 || bytes.length > 640) throw new Error("Invalid live text");
            break;
        case Component.Scoreboard:
            data.name = r.string(); data.score = r.i32(); data.ping = r.u16(); data.bot = r.u8();
            if (data.bot > 1) throw new Error("Invalid scoreboard flags");
            if (r.offset < r.bytes.length) {
                if (r.u8() !== 1) throw new Error("Unsupported map scoreboard extension");
                const limited = r.u8(), seconds = r.u32();
                if (limited > 1 || seconds > 2147483647 || data.name || data.score || data.ping || data.bot)
                    throw new Error("Invalid map scoreboard state");
                data.mapTime = { limited: !!limited, seconds };
            }
            break;
        case Component.AnimationLayers: {
            const count = r.u8(), slots = new Set();
            if (count > 15) throw new Error("Invalid animation layers");
            data.layers = Array.from({ length: count }, () => {
                const slot = r.u8(), sequence = r.i32(), cycle = r.u16() / 65535, weight = r.u16() / 65535;
                if (slot > 14 || slots.has(slot) || sequence < 0 || sequence > 16383) throw new Error("Invalid animation layer");
                slots.add(slot); return { slot, sequence, cycle, weight };
            });
            data.time = r.u32();
            break;
        }
        case Component.Animation:
            data.sequence = r.i32(); data.cycle = r.u16() / 65535; data.rate = r.f32();
            const count = r.u8();
            if (count > 24 || Math.abs(data.rate) > 128) throw new Error("Invalid animation");
            data.poses = Array.from({ length: count }, () => r.u16() / 65535);
            if (r.offset < r.bytes.length) {
                data.flags = r.u8();
                if (data.flags > 31 || ((data.flags & 4) && !(data.flags & 1)) || ((data.flags & 8) && (data.flags & 7)) || ((data.flags & 16) && !(data.flags & 8))) throw new Error('Invalid client animation flags');
                if (data.flags & 4) { data.startTime = r.u32(); data.startCycle = r.u16() / 65535; }
                if (data.flags & 8) {
                    data.anchorTime = r.u32(); data.cycleRate = r.f32();
                    if (Math.abs(data.cycleRate) > 4096) throw new Error('Invalid animation cycle rate');
                    data.eventParity = r.u8();
                    if (data.eventParity > 63) throw new Error('Invalid animation event parity');
                }
            }
            break;
        case Component.Building:
            data.level = r.u8(); data.mode = r.u8(); data.flags = r.u16(); data.progress = r.f32();
            if (data.progress < 0 || data.progress > 1) throw new Error("Invalid building progress");
            break;
        case Component.Light:
            data.kind = r.u8(); data.flags = r.u8(); data.color = r.u32(); data.radius = r.f32();
            data.exponent = r.u8(); if (data.exponent > 127) data.exponent -= 256;
            data.style = r.u8(); data.fov = r.f32(); data.near = r.f32(); data.far = r.f32();
            if (data.kind > 1 || data.flags > 7 || data.radius < 0 || data.fov < 0 || data.fov > 179 || data.near < 0 || data.far < data.near) throw new Error("Invalid light");
            break;
        case Component.Particle: {
            data.effect = r.u32(); data.flags = r.u8(); data.startTime = r.u32();
            data.ownerIndex = r.u32(); data.ownerGeneration = r.u32(); data.attachType = r.u8(); data.attachment = r.u16();
            data.offset = [r.i32(), r.i32(), r.i32()].map(v => v / 16);
            const count = r.u8(), seen = new Set(); data.controlPoints = [];
            if ((!data.effect && data.flags !== 0) || data.flags > 15 || data.attachType > 6 || count > 63 || data.ownerIndex > 65535 || (!!data.ownerIndex !== !!data.ownerGeneration)) throw new Error("Invalid particle");
            for (let i = 0; i < count; i++) {
                const id = r.u8(), parent = r.u8(), index = r.u16(), generation = r.u32();
                if (!id || id > 63 || parent > 63 || seen.has(id) || (!!index !== !!generation)) throw new Error("Invalid particle control point");
                seen.add(id); data.controlPoints.push({ id, parent, index, generation, ...(index ? {} : { position: [r.f32(), r.f32(), r.f32()] }) });
            }
            if (data.flags & 8) {
                if (r.u8() !== 1 || data.flags !== 9 || data.attachType !== 2 || count || !data.ownerIndex) throw new Error('Invalid gas manager');
                data.gasSeed = r.u16(); const gasCount = r.u8(); data.gasPoints = []; const ids = new Set();
                if (data.gasSeed > 9999 || gasCount > 20) throw new Error('Invalid gas manager');
                for (let i = 0; i < gasCount; i++) {
                    const id = r.u8(), tick = r.u32(), startTime = r.u32();
                    if (id > 29 || !tick || tick > 2147483647 || ids.has(id)) throw new Error('Invalid gas point');
                    ids.add(id); data.gasPoints.push({ id, tick, startTime });
                }
            }
            break;
        }
        case Component.Effects:
            data.ownerIndex = r.u32(); data.ownerGeneration = r.u32(); data.item = r.u16(); data.quality = r.u8(); data.team = r.u8(); data.water = r.u8(); data.weaponState = r.u8(); data.flags = r.u16();
            data.targetIndex = r.u32(); data.targetGeneration = r.u32(); data.charge = r.f32(); data.tauntItem = r.u16();
            data.unusual = r.u32(); data.staticParticle = r.u32(); data.taunt = r.u32(); data.offset = r.f32(); data.useHead = r.u8(); data.fireTime = r.u32();
            if (data.ownerIndex > 65535 || (!!data.ownerIndex !== !!data.ownerGeneration) || data.targetIndex > 65535 || (!!data.targetIndex !== !!data.targetGeneration) || data.team > 5 || data.water > 3 || data.flags > 255 || data.useHead > 1 || data.charge < 0 || data.charge > 1) throw new Error("Invalid client effect state");
            if (r.offset < r.bytes.length) {
                const version = r.u8();
                if (version !== 1 && version !== 2 && version !== 3 && version !== 4) throw new Error('Unsupported client effect extension');
                data.nativeClass = r.string(); data.clientFlags = r.u32();
                data.health = r.u32(); data.maxHealth = r.u32();
                data.objectState = r.u8(); data.objectMode = r.u8(); data.upgrade = r.u8(); data.flagStatus = r.u8();
                data.stunFlags = r.u16(); data.airDash = r.u8(); data.disguiseTeam = r.u8();
                data.energy = r.f32(); data.maxSpeed = r.f32(); data.objectFlags = r.u32();
                data.flagTrail = r.u8(); data.paperEffect = r.string();
                const count = r.u8(), seen = new Set(); data.targets = [];
                if (!/^[A-Za-z_][A-Za-z0-9_]{0,126}$/.test(data.nativeClass)
                    || data.clientFlags > 524287 || data.health > 2147483647 || data.maxHealth > 2147483647
                    || data.disguiseTeam > 3 || data.energy < 0 || data.energy > 100
                    || data.maxSpeed < 0 || data.maxSpeed > 65536 || data.flagTrail > 3
                    || encoder.encode(data.paperEffect).length > 128 || count > 64) throw new Error('Invalid client effect extension');
                for (let i = 0; i < count; i++) {
                    const role = r.u8(), index = r.u16(), generation = r.u32(), key = `${role}:${index}`;
                    if (role < 1 || role > (version >= 2 ? 6 : 4) || !index || !generation || seen.has(key)) throw new Error('Invalid client effect target');
                    seen.add(key); data.targets.push({ role, index, generation });
                }
                if (version >= 2) {
                    data.visualFlags = r.u16(); data.decapitations = r.u8(); data.killstreak = r.u32();
                    data.botSkill = r.u8(); data.kartState = r.u8(); data.kartDamage = r.u32(); data.tauntMoveSpeed = r.f32();
                    data.launchTime = r.u32(); data.campaignEvent = r.u32(); data.campaignState = r.u8();
                    data.killstreakEffect = r.u16(); data.killstreakColor = r.u8(); data.footstepType = r.u32(); data.footstepFlags = r.u8();
                    if (data.visualFlags > 15 || data.killstreak > 2147483647 || data.kartState > 15 || data.kartDamage > 2147483647
                        || Math.abs(data.tauntMoveSpeed) > 65536 || data.campaignState > 11 || data.killstreakColor > 7 || data.footstepFlags > 3) throw new Error('Invalid client visual state');
                }
                if (version >= 3) {
                    data.paint = r.u32(); data.paintAlt = r.u32();
                    if (data.paint > 0xffffff || data.paintAlt > 0xffffff) throw new Error('Invalid item paint');
                }
                if (version >= 4) {
                    data.equipmentKind = r.u8(); data.itemStyle = r.u32();
                    if (data.equipmentKind > 2 || data.itemStyle !== 0xffffffff && data.itemStyle > 254)
                        throw new Error('Invalid equipment bodygroup state');
                }
            }
            break;
        case Component.Sound:
            data.sound = r.u32(); data.startTime = r.u32(); data.ownerIndex = r.u32(); data.ownerGeneration = r.u32();
            data.channel = r.u16(); data.flags = r.u16(); data.volume = r.f32(); data.level = r.u8(); data.pitch = r.u8();
            data.dsp = r.offset < r.bytes.length ? r.u16() : 0;
            if (data.dsp > 255) throw new Error("Invalid sound DSP preset");
            if (!data.sound || data.ownerIndex > 65535 || (!!data.ownerIndex !== !!data.ownerGeneration) || data.channel > 255 || data.flags > 8191 || data.volume < 0 || data.volume > 1 || !data.pitch) throw new Error("Invalid sound state");
            break;
        case Component.Event:
            data.kind = r.u8(); data.startTime = r.u32(); data.ownerIndex = r.u32(); data.ownerGeneration = r.u32(); data.weapon = r.u16(); data.mode = r.u8(); data.seed = r.u8(); data.flags = r.u8(); data.spread = r.f32(); data.normal = [r.f32(), r.f32(), r.f32()]; data.customParticle = r.u32();
            if (data.kind > 5 || data.ownerIndex > 65535 || (!!data.ownerIndex !== !!data.ownerGeneration) || data.mode > 1 || data.flags > 31 || data.spread < 0 || data.spread > 1) throw new Error("Invalid client effect event");
            break;
        case Component.PlayerAnimation:
            data.event = r.u8(); data.data = r.u32(); data.startTime = r.u32(); data.serial = r.u32();
            if (data.event > 40 || data.data > 65535 || !data.serial) throw new Error("Invalid player animation event");
            break;
        case Component.Ragdoll:
            data.playerIndex = r.u32(); data.playerGeneration = r.u32(); data.playerClass = r.u8(); data.team = r.u8(); data.flags = r.u8(); data.startTime = r.u32();
            data.velocity = [r.f32(), r.f32(), r.f32()]; data.force = [r.f32(), r.f32(), r.f32()];
            if (data.playerIndex > 65535 || (!!data.playerIndex !== !!data.playerGeneration) || data.playerClass < 1 || data.playerClass > 9 || data.team > 3 || data.flags > 127) throw new Error("Invalid ragdoll state");
            break;
        case Component.Spray:
            data.crc = r.u32(); data.position = [r.f32(), r.f32(), r.f32()]; data.normal = [r.f32(), r.f32(), r.f32()];
            data.hitIndex = r.u32(); data.hitGeneration = r.u32(); data.startTime = r.u32(); data.serial = r.u32();
            const normalLength = data.normal.reduce((sum, v) => sum + v * v, 0);
            if (data.hitIndex > 65535 || (!!data.hitIndex !== !!data.hitGeneration) || (data.crc && (!data.serial || normalLength < 0.5 || normalLength > 1.5))) throw new Error("Invalid spray state");
            break;
        case Component.Sprite:
            data.frame = r.f32(); data.framerate = r.f32(); data.scale = r.f32(); data.scaleTime = r.f32(); data.brightnessTime = r.f32();
            data.glowProxySize = r.f32(); data.hdrScale = r.f32(); data.brightness = r.u8(); data.renderFX = r.u8(); data.flags = r.u8(); data.attachment = r.u8();
            data.attachedIndex = r.u32(); data.attachedGeneration = r.u32();
            if (data.frame < 0 || data.frame > 256 || data.framerate < 0 || data.framerate > 60 || data.scale < 0 || data.scale > 64
                || data.scaleTime < 0 || data.brightnessTime < 0 || data.glowProxySize < 0 || data.glowProxySize > 64 || data.hdrScale < 0
                || data.flags > 7 || data.attachedIndex > 65535 || (!!data.attachedIndex !== !!data.attachedGeneration)) throw new Error("Invalid sprite state");
            if (data.flags & 2) {
                data.trail = { lifetime: r.f32(), startWidth: r.f32(), endWidth: r.f32(), startWidthVariance: r.f32(), textureRes: r.f32(),
                    minFadeLength: r.f32(), skyboxOrigin: [r.f32(), r.f32(), r.f32()], skyboxScale: r.f32() };
                const t = data.trail;
                if (t.lifetime < 0 || t.startWidth < 0 || t.startWidthVariance < 0 || t.textureRes < 0 || t.minFadeLength < 0 || t.skyboxScale <= 0) throw new Error("Invalid sprite trail state");
            }
            if (data.flags & 4) {
                data.frameTime = r.u32(); data.maxFrame = r.f32(); data.frameFlags = r.u8();
                if (data.maxFrame < 0 || data.maxFrame > 255 || data.frameFlags > 3) throw new Error('Invalid sprite frame anchor');
            }
            break;
        case Component.Projectile:
            data.team = r.u8(); data.flags = r.u8(); data.variant = r.u8();
            if (data.team > 5 || data.flags > 3 || data.variant > 2) throw new Error("Invalid projectile effects");
            data.nativeClass = r.offset === r.bytes.length ? '' : r.string();
            if (data.nativeClass && !/^[A-Za-z_][A-Za-z0-9_]{0,126}$/.test(data.nativeClass)
                || !data.nativeClass && r.bytes.length !== 3) throw new Error("Invalid projectile client class");
            break;
        default: throw new Error("Unknown component");
    }
    r.end(); return data;
}

export function decodeRecords(payload) {
    const r = new Reader(payload), count = r.u16(), records = [];
    if (count > (r.bytes.length - r.offset) / 3) throw new Error("Invalid record count");
    for (let i = 0; i < count; i++) {
        const item = new Reader(r.take(r.u16())), operation = item.u8();
        if (operation === 4) {
            const kind = item.u8(), id = item.u32(), value = item.string();
            validateDictionary(kind, id, value);
            records.push({ operation, kind, id, value });
        } else {
            if (operation < 1 || operation > 3) throw new Error("Unknown record operation");
            const index = item.u32(), generation = item.u32(), components = new Map();
            if (!index || index > 65535 || !generation) throw new Error("Invalid entity identity");
            const sampleTime = operation === 3 ? 0 : item.u32();
            if (operation !== 3) {
                const mask = item.u32();
                if (!mask || mask & ~33554431) throw new Error(`Unsupported component mask 0x${mask.toString(16)} (supported 0x1ffffff)`);
                for (const kind of Object.values(Component)) {
                    if (!(mask & kind)) continue;
                    const data = item.take(item.u16()).slice(); decodeComponent(kind, data); components.set(kind, data);
                }
                if (operation === 1 && (!components.has(1) || !components.has(2))) throw new Error("Create requires transform and appearance");
            }
            records.push({ operation, index, generation, key: `${index}:${generation}`, sampleTime, components });
        }
        item.end();
    }
    r.end(); return records;
}

export function encodeRecords(records) {
    const writer = new Writer().u16(records.length);
    for (const record of records) {
        const item = new Writer().u8(record.operation);
        if (record.operation === 4) item.u8(record.kind).u32(record.id).string(record.value);
        else {
            item.u32(record.index).u32(record.generation);
            if (record.operation !== 3) {
                item.u32(record.sampleTime).u32([...record.components.keys()].reduce((a, b) => a | b, 0));
                for (const [kind, data] of [...record.components].sort((a, b) => a[0] - b[0])) {
                    decodeComponent(kind, data); item.u16(data.length).raw(data);
                }
            }
        }
        const bytes = item.finish(); writer.u16(bytes.length).raw(bytes);
    }
    return writer.finish();
}

export function decodeStatus(frame) {
    const r = new Reader(frame.payload), status = r.u8(), serverId = r.u32();
    if (status > 7) throw new Error("Invalid stream status");
    const map = { name: r.string(), id: r.string(), revision: r.string(), assetSet: r.string(), contentGeneration: r.string() };
    r.end(); return { status, serverId, epoch: frame.epoch, map };
}

// Browser controls contain no identity fields: the relay derives those from Steam authentication.
export function viewerPosition(position, angles) {
    if (!Array.isArray(position) || position.length !== 3 || position.some(v => !Number.isFinite(v) || Math.abs(v) > 32768))
        throw new Error('Camera outside world bounds');
    if (angles !== undefined && (!Array.isArray(angles) || angles.length !== 3 || angles.some(v => !Number.isFinite(v) || Math.abs(v) > 180)))
        throw new Error('Invalid camera angles');
    const payload = new Uint8Array(angles === undefined ? 12 : 24), view = new DataView(payload.buffer);
    position.forEach((v, axis) => view.setFloat32(axis * 4, v, true));
    angles?.forEach((v, axis) => view.setFloat32(12 + axis * 4, v, true));
    return payload;
}
export function viewerChat(text) {
    text = String(text ?? '').trim();
    if (!text || /[\p{Cc}\p{Cf}]/u.test(text) || encoder.encode(text).length > 160)
        throw new Error('Enter a message of at most 160 UTF-8 bytes without control characters.');
    return new Writer().string(text).finish();
}
